import axiosInstance from '../axios';

// --- ENDPOINTS DE CLIENTE ---

/**
 * Busca todos os clientes.
 * @returns {Promise<Array>} Uma promessa que resolve para um array de objetos de cliente.
 */
export const getClientes = async () => {
    const response = await axiosInstance.get('/clientes');
    return response.data;
};

/**
 * Busca um único cliente pelo ID.
 * @param {string} id - O ID do cliente.
 * @returns {Promise<Object>} Uma promessa que resolve para um objeto de cliente.
 */
export const getClienteById = async (id) => {
    const response = await axiosInstance.get(`/clientes/${id}`);
    return response.data;
};

/**
 * Cria um novo cliente ou atualiza um existente.
 * @param {Object} cliente - O objeto do cliente a ser salvo. Se tiver 'id', atualizará; caso contrário, criará.
 * @returns {Promise<Object>} Uma promessa que resolve para o objeto do cliente salvo.
 */
export const saveCliente = async (cliente) => {
    if (cliente.id) {
        return await axiosInstance.put(`/clientes/${cliente.id}`, cliente);
    }
    return await axiosInstance.post('/clientes', cliente);
};

/**
 * Deleta um cliente pelo ID.
 * @param {string} id - O ID do cliente a ser deletado.
 * @returns {Promise<void>} Uma promessa que resolve quando o cliente é deletado.
 */
export const deleteCliente = async (id) => {
    return await axiosInstance.delete(`/clientes/${id}`);
};

// --- ENDPOINTS DE ESTAÇÃO ---

/**
 * Busca todas as estações para um determinado cliente.
 * @param {string} clienteId - O ID do cliente.
 * @returns {Promise<Array>} Uma promessa que resolve para um array de objetos de estação.
 */
export const getEstacoesByCliente = async (clienteId) => {
    const response = await axiosInstance.get(`/clientes/${clienteId}/estacoes`);
    return response.data;
};

/**
 * Cria uma nova estação para um cliente específico.
 * @param {string} clienteId - O ID do cliente.
 * @param {Object} estacaoData - Os dados para a nova estação.
 * @returns {Promise<Object>} Uma promessa que resolve para o objeto da estação criada.
 */
export const createEstacaoForCliente = async (clienteId, estacaoData) => {
    // Nota: A especificação Swagger fornecida mostrava "string" para POST: /api/clientes/{clienteId}/estacoes
    // e "object" para POST: /api/clientes/{clienteId}/estacoes/criar.
    // Presumi que `estacaoData` é um objeto para criar uma estação,
    // e `criar` é o endpoint específico para criação detalhada da estação.
    // Se o seu backend lida de forma diferente, pode ser necessário ajustar.
    const response = await axiosInstance.post(
        `/clientes/${clienteId}/estacoes/criar`,
        estacaoData
    );
    return response.data;
};

/**
 * Atualiza uma estação existente para um determinado cliente.
 * @param {string} clienteId - O ID do cliente.
 * @param {string} estacaoId - O ID da estação a ser atualizada.
 * @param {Object} estacaoData - Os dados atualizados da estação.
 * @returns {Promise<Object>} Uma promessa que resolve para o objeto da estação atualizada.
 */
export const updateEstacaoForCliente = async (
    clienteId,
    estacaoId,
    estacaoData
) => {
    const response = await axiosInstance.put(
        `/clientes/${clienteId}/estacoes/${estacaoId}`,
        estacaoData
    );
    return response.data;
};

/**
 * Deleta uma estação específica para um determinado cliente.
 * @param {string} clienteId - O ID do cliente.
 * @param {string} estacaoId - O ID da estação a ser deletada.
 * @returns {Promise<void>} Uma promessa que resolve quando a estação é deletada.
 */
export const deleteEstacaoForCliente = async (clienteId, estacaoId) => {
    return await axiosInstance.delete(
        `/clientes/${clienteId}/estacoes/${estacaoId}`
    );
};

/**
 * Atualiza o status (ativo/inativo) de uma estação específica para um determinado cliente.
 * @param {string} clienteId - O ID do cliente.
 * @param {string} estacaoId - O ID da estação a ser atualizada.
 * @param {boolean} status - O novo status (true para ativo, false para inativo).
 * @returns {Promise<Object>} Uma promessa que resolve para o objeto da estação atualizada.
 */
export const updateEstacaoStatus = async (clienteId, estacaoId, status) => {
    const response = await axiosInstance.patch(
        `/clientes/${clienteId}/estacoes/${estacaoId}/status`,
        null,
        {
            params: { status: status }, // Envia o status como um parâmetro de query
        }
    );
    return response.data;
};

// --- ENDPOINTS DE PROTOCOLO ---

/**
 * Busca todos os protocolos disponíveis para um cliente específico.
 * @param {string} clienteId - O ID do cliente.
 * @returns {Promise<Array>} Uma promessa que resolve para um array de objetos de protocolo.
 */
export const getProtocolosByCliente = async (clienteId) => {
    const response = await axiosInstance.get(
        `/clientes/${clienteId}/protocolos`
    );
    return response.data;
};

/**
 * Busca todos os protocolos globais (se /api/clientes/protocolos for um endpoint geral).
 * Nota: Seu swagger também listou /api/clientes/protocolos sem ID de cliente,
 * presumindo que este seja para protocolos globais/disponíveis, não específicos de um cliente.
 * Se for para listar protocolos para o cliente logado, esclareça o design da sua API.
 * @returns {Promise<Array>} Uma promessa que resolve para um array de todos os objetos de protocolo.
 */
export const getAllProtocols = async () => {
    const response = await axiosInstance.get(`/clientes/protocolos`);
    return response.data;
};

/**
 * Adiciona protocolos a um cliente.
 * @param {string} clienteId - O ID do cliente.
 * @param {Array<string>} protocoloIds - Um array de IDs de protocolo a serem adicionados.
 * @returns {Promise<Object>} Uma promessa que resolve após a operação.
 */
export const addProtocolsToClient = async (clienteId, protocoloIds) => {
    // Assume que o PUT /api/clientes/{clienteId}/protocolos espera um array de strings (IDs de protocolo)
    const response = await axiosInstance.put(
        `/clientes/${clienteId}/protocolos`,
        protocoloIds
    );
    return response.data;
};

/**
 * Cria novos protocolos para um cliente.
 * @param {string} clienteId - O ID do cliente.
 * @param {Array<string>} protocoloData - Um array de dados de protocolo (presumindo que seja um array de IDs ou nomes).
 * @returns {Promise<Object>} Uma promessa que resolve após a operação.
 */
export const createProtocolsForClient = async (clienteId, protocoloData) => {
    // Assume que o POST /api/clientes/{clienteId}/protocolos espera um array de strings (dados de protocolo)
    const response = await axiosInstance.post(
        `/clientes/${clienteId}/protocolos`,
        protocoloData
    );
    return response.data;
};

/**
 * Deleta um protocolo específico de um cliente.
 * @param {string} clienteId - O ID do cliente.
 * @param {string} protocoloId - O ID do protocolo a ser deletado.
 * @returns {Promise<void>} Uma promessa que resolve quando o protocolo é deletado.
 */
export const deleteProtocoloFromClient = async (clienteId, protocoloId) => {
    return await axiosInstance.delete(
        `/clientes/${clienteId}/protocolos/${protocoloId}`
    );
};

/**
 * Salva dados obrigatórios do cliente e dispara envio do código de validação por e-mail.
 * @param {string} id - O ID do cliente.
 * @param {Object} body - Dados: email, documentType, document, zipCode, street, number, complement, neighborhood, city, state
 */
export const configurarCliente = async (id, body) => {
    await axiosInstance.put(`/clientes/${id}/configurar`, body);
};

/**
 * Valida o código recebido por e-mail e marca o cliente como VALIDADO.
 * @param {string} id - O ID do cliente.
 * @param {string} code - O código de 6 dígitos recebido por e-mail.
 * @returns {Promise<Object>} Objeto com { status: "VALIDADO" }
 */
export const validarEmailCliente = async (id, code) => {
    const response = await axiosInstance.post(`/clientes/${id}/validar-email`, { code });
    return response.data;
};

/**
 * Salva logo/background de e-mail (branding) do cliente.
 * @param {string} id - O ID do cliente.
 * @param {Object} body - { logoBase64, emailBackgroundBase64 } (data URI)
 */
export const updateClienteBranding = async (id, body) => {
    const response = await axiosInstance.put(`/clientes/${id}/branding`, body);
    return response.data;
};

// --- EXPORTAÇÃO DE TODAS AS FUNÇÕES ---
const apiClientes = {
    getClientes,
    getClienteById,
    saveCliente,
    deleteCliente,
    configurarCliente,
    validarEmailCliente,
    updateClienteBranding,
    getEstacoesByCliente,
    createEstacaoForCliente,
    updateEstacaoForCliente,
    deleteEstacaoForCliente,
    updateEstacaoStatus,
    getProtocolosByCliente,
    getAllProtocols, // Se este endpoint é realmente para TODOS os protocolos
    addProtocolsToClient,
    createProtocolsForClient,
    deleteProtocoloFromClient,
};

export default apiClientes;
